import React, { memo, useCallback, useMemo } from 'react';
import classNames from 'classnames';
import { useActivityStatusMapping } from '@punchcard/core/utils/statusMappingConfig';
import { ASSESSMENT_STATUS } from '@punchcard/core/constants/statusConstants';

interface IProps {
	attempt: TeacherAssessmentAttemptDTO;
	assessment: TeacherCourseActivityDTO;
	grade: string;
	currentActivityItem?: TeacherCourseActivityDTO;
	handleActivityClick?: (index: number | null, currentActivity: currentActivityAssessment, attemptId: number) => void;
}

const AssessmentAttempt = memo(function AssessmentAttempt({
	attempt,
	grade,
	assessment,
	currentActivityItem,
	handleActivityClick,
}: IProps) {
	const statusMap = useActivityStatusMapping();

	// Memoize status configuration to avoid recalculation
	const statusConfig = useMemo(() => statusMap[attempt.statusCode] || {}, [statusMap, attempt.statusCode]);

	const IconComponent = statusConfig.icon;

	// Memoize active state calculation
	const isActive = useMemo(() =>
		currentActivityItem?.id === assessment?.id && currentActivityItem?.reAttemptNumber === attempt?.attemptNumber,
	[currentActivityItem?.id, currentActivityItem?.reAttemptNumber, assessment?.id, attempt?.attemptNumber]
	);

	// Memoize click handler to prevent unnecessary re-renders
	const handleClick = useCallback(() => {
		handleActivityClick?.(attempt?.attemptId ?? null, { type: 'Assessment', item: assessment }, attempt?.attemptNumber);
	}, [handleActivityClick, attempt?.attemptId, attempt?.attemptNumber, assessment]);

	return (
		<button
			className={classNames('activity ', { 'active': isActive })}
			onClick={handleClick}
		>
			<div className="me-2 ms-3 d-flex justify-content-between align-items-center">
				{IconComponent && <IconComponent style={{ width: 18, height: 18 }} className={classNames('me-2', statusConfig.className)} />}
				Attempt {attempt.attemptNumber}
			</div>
			<div className={classNames(statusConfig.className)}>{attempt.statusCode === ASSESSMENT_STATUS.IN_PROGRESS ? statusConfig.status : grade}</div>
		</button>
	);
});

export default AssessmentAttempt;